import { mapToCamelCase } from '@consigli/utils';
import { z } from 'zod';
import { organizationSchema, OrganizationType } from './organization';
import { getPaginationResponseSchema } from './pagination';
import { Country, ProjectConstructionType, ProjectInvoiceTo, ProjectSpecification, } from './project-properties';
export const projectPersonSchema = z
    .object({
    id: z.string().uuid(),
    first_name: z.string(),
    last_name: z.string(),
    // Loose rather than .email(): '' is a real value in the database. See above
    // — nothing populates this.
    email: z.string().optional(),
})
    // Its own transform, as projectAccessSchema has, so ProjectPerson describes
    // what callers hold rather than the wire shape. Idempotent.
    .transform(mapToCamelCase);
export const projectSchema = z
    .object({
    id: z.number().int(),
    name: z.string(),
    organization_id: organizationSchema.innerType().shape.id,
    organization_name: z.string(),
    organization_type: z.nativeEnum(OrganizationType),
    address: z.string().nullable(),
    country: z.nativeEnum(Country),
    specifications: z.array(z.nativeEnum(ProjectSpecification)),
    construction_types: z.array(z.nativeEnum(ProjectConstructionType)),
    container_name: z.string().uuid(),
    updated_at: z.string().datetime(),
    created_at: z.string().datetime(),
    billing_information: z.string().nullable(),
    billing_address: z.string().nullable(),
    org_number: z.number().int().nullable(),
    invoice_to: z.nativeEnum(ProjectInvoiceTo),
    oracle_id: z.string().nullable(),
    famac_api_token: z.string().nullish(),
    active_services: z.array(z.string()),
    available_services: z.array(z.string()),
    available_services_override: z.array(z.string()),
    // External services (apps outside monoweb) recorded as used on the project.
    // Read-only, and optional so a core-api that does not send it yet still parses.
    used_external_services: z.array(z.string()).optional(),
    effective_services: z.array(z.string()),
    package_count: z.number().int(),
    // Serialised with isoformat(), so it ends in +00:00 rather than DRF's Z.
    last_uploaded_package: z.string().datetime({ offset: true }).nullable(),
    unpublished_packages: z.object({
        document_packages: z.array(z.string()),
        layout_packages: z.array(z.string()),
    }),
    is_hidden: z.boolean(),
    created_by: z.string().uuid().nullable(),
    package_types: z.array(z.string()).optional(),
    people: z.array(projectPersonSchema).optional(),
    // Optional, like used_external_services, so a core-api that does not send
    // them yet still parses.
    project_manager_email: z.string().nullish(),
    project_manager_name: z.string().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const getProjectsResponseSchema = getPaginationResponseSchema(projectSchema);
export const getProjectsRequestSchema = z
    .object({
    page: z.union([z.number().int(), z.literal('all')]),
    page_size: z.number().int().nullish(),
    search: z.string().nullish(),
    is_hidden: z.boolean().nullish(),
    ordering: z.string().nullish(),
    package_types: z.string().nullish(),
    // Comma-separated, like package_types: the backend filter is a
    // UUIDInFilter, so one creator and several share a wire format.
    created_by: z.string().nullish(),
    // Comma-separated organization ids, a UUIDInFilter on the backend as well.
    organization: z.string().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const createProjectResponseSchema = projectSchema;
export const createProjectRequestSchema = projectSchema
    .innerType()
    .pick({
    name: true,
    address: true,
    country: true,
    specifications: true,
    construction_types: true,
    organization_id: true,
    billing_information: true,
    billing_address: true,
    org_number: true,
    invoice_to: true,
    oracle_id: true,
    available_services_override: true,
})
    .strict()
    .transform(mapToCamelCase);
/**
 * `.strict()`, so a field absent here is rejected before a request is made.
 *
 * The five settings fields are picked for the project Settings tab. The backend
 * has always accepted them on `PATCH`: none sit in `ProjectSerializer`'s
 * `read_only_fields`, `ProjectUpdateSerializer` pins only `organization_id`, and
 * `createProjectRequestSchema` above already writes every one of them.
 *
 * Widening what may be *sent* is not widening who may send it — see
 * `useCanManageProject`, which is what decides whether the fields are editable.
 */
export const updateProjectRequestSchema = projectSchema
    .innerType()
    .pick({
    name: true,
    address: true,
    country: true,
    construction_types: true,
    oracle_id: true,
    is_hidden: true,
    available_services_override: true,
})
    .partial()
    .strict()
    .transform(mapToCamelCase);
export const getProjectRequestSchema = z
    .object({
    projectId: z.number().int(),
    isHidden: z.boolean().optional(),
})
    .strict()
    .transform(mapToCamelCase);
